import React from 'react'
import type { FC, HTMLAttributes } from 'react'
import styles from './typography.module.css'

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'caption'
  | 'overline'
  | 'button'
  | 'inherit'

export type TypographyColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'success'
  | 'textSecondary'
  | 'textDisabled'
  | 'inherit'
  | (string & {})

export type TypographyAlign = 'left' | 'center' | 'right' | 'justify' | 'inherit'

export interface TypographyProps extends HTMLAttributes<HTMLElement> {
  variant?: TypographyVariant
  color?: TypographyColor
  component?: string | React.ComponentType
  display?: 'block' | 'inline' | 'flex'
  noWrap?: boolean
  gutterBottom?: boolean
  align?: TypographyAlign
  ref?: React.Ref<HTMLElement>
}

const variantElementMap: Record<string, string> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  caption: 'span',
  overline: 'span',
  button: 'span',
  inherit: 'span',
}

const variantClassMap: Record<string, string> = {
  h1: styles.h1,
  h2: styles.h2,
  h3: styles.h3,
  h4: styles.h4,
  h5: styles.h5,
  h6: styles.h6,
  subtitle1: styles.subtitle1,
  subtitle2: styles.subtitle2,
  body1: styles.body1,
  body2: styles.body2,
  caption: styles.caption,
  overline: styles.overline,
  button: styles.button,
}

const colorClassMap: Record<string, string> = {
  primary: styles.colorPrimary,
  secondary: styles.colorSecondary,
  error: styles.colorError,
  success: styles.colorSuccess,
  textSecondary: styles.colorTextSecondary,
  textDisabled: styles.colorTextDisabled,
  'text.secondary': styles.colorTextSecondary,
  'text.disabled': styles.colorTextDisabled,
  'text.primary': '',
}

const alignClassMap: Record<string, string> = {
  center: styles.alignCenter,
  right: styles.alignRight,
  justify: styles.alignJustify,
}

export const Typography: FC<TypographyProps> = ({
  variant = 'body1',
  color,
  component,
  display,
  noWrap,
  gutterBottom,
  align,
  className,
  style,
  ...rest
}) => {
  const Component = component ?? variantElementMap[variant] ?? 'span'

  const classes = [
    styles.typography,
    variant !== 'inherit' ? variantClassMap[variant] : undefined,
    color && colorClassMap[color] ? colorClassMap[color] : undefined,
    noWrap ? styles.noWrap : undefined,
    gutterBottom ? styles.gutterBottom : undefined,
    align && align !== 'left' && align !== 'inherit' ? alignClassMap[align] : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const computedStyle = {
    ...style,
    ...(display ? { display } : undefined),
    ...(color && !colorClassMap[color] && color !== 'inherit' ? { color } : undefined),
  }

  const props = {
    className: classes,
    style: Object.keys(computedStyle).length > 0 ? computedStyle : undefined,
    ...rest,
  }
  return React.createElement(Component as string, props)
}
